import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Video } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { CoachingApi, CapstoneApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './CoachingMentoring.css';

const personName = (u) => u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || 'Mentee';

const sessionLabel = (status) => ({
  requested: 'New request',
  scheduled: 'New request',
  confirmed: 'Accepted',
  'in-progress': 'Live',
  completed: 'Done',
  cancelled: 'Declined',
}[status] || status);

const MentorStudio = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [studio, setStudio] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [notice, setNotice] = useState('');
  const [capstoneQueue, setCapstoneQueue] = useState([]);
  const [reviewDrafts, setReviewDrafts] = useState({});

  const load = useCallback(async () => {
    const res = await CoachingApi.getMentorStudio(token);
    if (!res.success) {
      setError(res.message || 'Not approved yet.');
      setStudio(res.data ? { mentor: res.data, mentorships: [], sessions: [] } : null);
      return;
    }
    setError('');
    setStudio(res.data);
    if (res.data?.mentor?.status === 'approved') {
      try {
        const queueRes = await CapstoneApi.reviewQueue(token);
        setCapstoneQueue(queueRes.data || []);
      } catch {
        setCapstoneQueue([]);
      }
    }
  }, [token]);

  const submitCapstoneReview = async (id, approved) => {
    setBusyId(id);
    try {
      await CapstoneApi.review(token, id, { approved, feedback: reviewDrafts[id] || '' });
      setNotice(approved ? 'Capstone approved.' : 'Sent back for revision.');
      await load();
    } catch (err) {
      setNotice(err.message || 'Could not submit the review.');
    } finally {
      setBusyId('');
    }
  };

  useEffect(() => {
    document.documentElement.classList.add('qn-home-html');
    document.body.classList.add('qn-home-body');
    return () => {
      document.documentElement.classList.remove('qn-home-html');
      document.body.classList.remove('qn-home-body');
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { notice: 'Sign in to open your mentor studio.' } });
      return;
    }
    let cancelled = false;
    load().catch((err) => {
      if (!cancelled) setError(err.message || 'Could not load studio.');
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, load, navigate]);

  const run = async (id, fn, thenJoin) => {
    setBusyId(id);
    setNotice('');
    try {
      const res = await fn();
      setNotice(res.message || 'Updated.');
      await load();
      if (thenJoin && res.data?.meetingLink) navigate(res.data.meetingLink);
    } catch (err) {
      setNotice(err.message || 'Could not update the session.');
    } finally {
      setBusyId('');
    }
  };

  const mentor = studio?.mentor;
  const mentees = studio?.mentorships || [];
  const sessions = studio?.sessions || [];
  const incoming = sessions.filter((s) => ['requested', 'scheduled'].includes(s.status));
  const live = sessions.filter((s) => ['confirmed', 'in-progress'].includes(s.status));
  const history = sessions.filter((s) => !['requested', 'scheduled', 'confirmed', 'in-progress'].includes(s.status));

  const renderSession = (s, actions) => (
    <li key={s._id}>
      <span>
        {s.title} · {personName(s.mentee)}
        <em className="cm-session-sub">
          {sessionLabel(s.status)}
          {s.scheduledDate ? ` · ${new Date(s.scheduledDate).toLocaleString()}` : ''}
        </em>
      </span>
      <span className="cm-session-right">
        {actions}
      </span>
    </li>
  );

  return (
    <div className="cm">
      <Header />
      <main className="cm-main">
        <header className="cm-head cm-head--row">
          <div>
            <p>Mentor studio</p>
            <h1>{mentor ? `${mentor.firstName} ${mentor.lastName}` : 'Your coaching desk'}</h1>
          </div>
          <Link to="/coachingmentoring" className="cm-btn-ghost">Public mentor list</Link>
        </header>

        {error && (
          <div className="cm-card">
            <p>{error}</p>
            <p className="cm-foot-note">
              <Link to="/coachingmentoring">Apply or check your application →</Link>
            </p>
          </div>
        )}

        {notice && <p className="cm-foot-note">{notice}</p>}

        {mentor && mentor.status === 'approved' && (
          <>
            <p className="cm-meta" style={{ marginBottom: '1.5rem' }}>
              <span>{mentor.title}</span>
              <span>{mentor.status}</span>
              <span>{mentor.email}</span>
            </p>

            <section className="cm-block">
              <h3>Capstone reviews ({capstoneQueue.length})</h3>
              {capstoneQueue.length === 0 ? (
                <p>No capstones waiting on your expertise right now.</p>
              ) : (
                <div className="cm-grid">
                  {capstoneQueue.map((c) => (
                    <article key={c._id} className="cm-card">
                      <h2>{c.title || c.course?.title}</h2>
                      <p>{personName(c.student)} · {c.course?.title} · {c.targetRole}</p>
                      {c.submission?.text && <p className="cm-foot-note">{c.submission.text.slice(0, 240)}{c.submission.text.length > 240 ? '…' : ''}</p>}
                      {c.submission?.artifactUrl && (
                        <p><a href={c.submission.artifactUrl} target="_blank" rel="noreferrer">View artifact →</a></p>
                      )}
                      {c.labResults?.length > 0 && (
                        <p className="cm-foot-note">
                          Bench: {c.labResults[c.labResults.length - 1].passed ? 'passed' : 'failed'} — {c.labResults[c.labResults.length - 1].critique?.slice(0, 200)}
                        </p>
                      )}
                      <textarea
                        rows={3}
                        placeholder="Feedback for the learner…"
                        value={reviewDrafts[c._id] || ''}
                        onChange={(e) => setReviewDrafts((prev) => ({ ...prev, [c._id]: e.target.value }))}
                        style={{ width: '100%', margin: '0.5rem 0' }}
                      />
                      <div className="cm-meta">
                        <button
                          type="button"
                          className="cm-apply"
                          disabled={busyId === c._id}
                          onClick={() => submitCapstoneReview(c._id, true)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="cm-btn-ghost"
                          disabled={busyId === c._id}
                          onClick={() => submitCapstoneReview(c._id, false)}
                        >
                          Needs revision
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="cm-block">
              <h3>Incoming requests ({incoming.length})</h3>
              {incoming.length === 0 ? (
                <p>No new requests. Students send them from the mentor list.</p>
              ) : (
                <ul className="cm-sessions">
                  {incoming.map((s) => renderSession(s, (
                    <>
                      <button
                        type="button"
                        className="cm-apply"
                        disabled={busyId === s._id}
                        onClick={() => run(s._id, () => CoachingApi.respondSession(token, s._id, 'accept'))}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="cm-btn-ghost"
                        disabled={busyId === s._id}
                        onClick={() => run(s._id, () => CoachingApi.respondSession(token, s._id, 'decline'))}
                      >
                        Decline
                      </button>
                    </>
                  )))}
                </ul>
              )}
            </section>

            <section className="cm-block">
              <h3>Live sessions ({live.length})</h3>
              {live.length === 0 ? (
                <p>Accepted sessions show up here. Start the room when you are ready — the mentee gets a Join button.</p>
              ) : (
                <ul className="cm-sessions">
                  {live.map((s) => renderSession(s, (
                    <>
                      {s.status === 'confirmed' && (
                        <button
                          type="button"
                          className="cm-apply"
                          disabled={busyId === s._id}
                          onClick={() => run(s._id, () => CoachingApi.startSession(token, s._id), true)}
                        >
                          Start live room
                        </button>
                      )}
                      {s.meetingLink && (
                        <Link to={s.meetingLink} className="cm-join">
                          <Video size={14} /> Join
                        </Link>
                      )}
                    </>
                  )))}
                </ul>
              )}
            </section>

            <section className="cm-block">
              <h3>Mentees ({mentees.length})</h3>
              {mentees.length === 0 ? (
                <p>They appear here after a student requests a session with you.</p>
              ) : (
                <div className="cm-grid">
                  {mentees.map((row) => (
                    <article key={row._id} className="cm-card">
                      <h2>{personName(row.mentee)}</h2>
                      <p>{row.primaryGoal || row.type || 'Mentorship'}</p>
                      <div className="cm-meta">
                        <span>{row.status || 'active'}</span>
                        {row.mentee?.email && <span>{row.mentee.email}</span>}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {history.length > 0 && (
              <section className="cm-block">
                <h3>Earlier sessions</h3>
                <ul className="cm-sessions">
                  {history.map((s) => renderSession(s, <em>{sessionLabel(s.status)}</em>))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MentorStudio;
